import { useRef, useState } from "react";
import { createPortal } from "react-dom";
import useFixedPortalMenu, { PORTAL_SHELL_STYLE } from "@/hooks/useFixedPortalMenu";
import { getPortalThemeProps } from "@/constants/themes";
import { getRoundLabel } from "@/utils/leagueFixturesByLeague";

const ALL = "__all__";

function ChevronGlyph() {
  return (
    <svg viewBox="0 0 16 16" aria-hidden="true">
      <path
        d="M4 6l4 4 4-4"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function CheckGlyph() {
  return (
    <svg viewBox="0 0 16 16" aria-hidden="true">
      <path
        d="M3.2 8.2l3.1 3.2 6.5-6.8"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export default function RoundSelect({
  roundEntries,
  selectedRound,
  onSelect,
  themeStyle,
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const close = () => setOpen(false);
  const { menuStyle, updatePosition } = useFixedPortalMenu({
    open,
    onClose: close,
    triggerRef,
    menuRef,
    minHeight: 120,
    maxHeightCap: 320,
  });

  if (!roundEntries?.length) return null;

  const options = [
    { key: ALL, label: "Tất cả vòng" },
    ...roundEntries.map(([round, matches]) => ({
      key: String(round),
      label: getRoundLabel(round, matches),
    })),
  ];

  const current =
    selectedRound == null
      ? options[0]
      : options.find((o) => o.key === String(selectedRound)) || options[0];

  const { theme: portalTheme, className: portalClassName } =
    getPortalThemeProps(rootRef.current);

  return (
    <div ref={rootRef} className="kbv-ltd-sb-select-wrap">
      <button
        ref={triggerRef}
        type="button"
        className={`kbv-ltd-sb-select kbv-ltd-sb-select-round ${open ? "is-open" : ""}`}
        aria-label="Chọn vòng đấu"
        aria-expanded={open}
        aria-haspopup="listbox"
        onClick={() => {
          setOpen((prev) => {
            if (prev) return false;
            updatePosition();
            return true;
          });
        }}
      >
        <span className="kbv-ltd-sb-select-label" title={current.label}>
          {current.label}
        </span>
        <span className={`kbv-ltd-sb-chevron ${open ? "is-open" : ""}`}>
          <ChevronGlyph />
        </span>
      </button>

      {open &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            className={portalClassName}
            data-theme={portalTheme}
            style={{ ...themeStyle, ...PORTAL_SHELL_STYLE }}
          >
            <div
              ref={menuRef}
              style={{ ...themeStyle, ...menuStyle }}
              className="kbv-ltd-sb-drop"
              role="listbox"
              aria-label="Danh sách vòng đấu"
            >
              <div className="kbv-ltd-sb-drop-list kbv-ltd-sb-drop-list-only">
                {options.map((opt) => {
                  const selected = opt.key === current.key;
                  return (
                    <button
                      key={opt.key}
                      type="button"
                      role="option"
                      aria-selected={selected}
                      onClick={() => {
                        onSelect?.(opt.key === ALL ? null : opt.key);
                        setOpen(false);
                      }}
                      className={`kbv-ltd-sb-drop-item ${selected ? "is-active" : ""}`}
                    >
                      <span className="kbv-ltd-sb-drop-name">{opt.label}</span>
                      {selected ? (
                        <span className="kbv-ltd-sb-drop-check">
                          <CheckGlyph />
                        </span>
                      ) : null}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}
